
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useFlaggedActivities, useDismissFlaggedActivity, useMarkFlaggedActivityAsInvestigating } from '@/hooks/useAdminAffiliate';
import { toast } from 'sonner';
import { MobileFlaggedActivityCard } from '@/components/admin/affiliate/MobileFlaggedActivityCard';
import { useIsMobile } from '@/hooks/use-mobile';

export const FlaggedActivityPage = () => {
  const { data: flaggedActivities, isLoading, refetch } = useFlaggedActivities();
  const dismissMutation = useDismissFlaggedActivity();
  const investigateMutation = useMarkFlaggedActivityAsInvestigating();
  const isMobile = useIsMobile();

  const handleDismiss = async (id: string) => {
    dismissMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Flag dismissed successfully");
        refetch();
      },
      onError: () => {
        toast.error("Failed to dismiss flag");
      }
    });
  };

  const handleInvestigate = async (id: string) => {
    investigateMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Marked as investigating");
        refetch();
      },
      onError: () => {
        toast.error("Failed to update status");
      }
    });
  };

  if (isLoading) {
    return <div className="p-8">Loading flagged activities...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Flagged Activity</h1>
        <p className="text-muted-foreground mt-1">Review potentially suspicious affiliate activities</p>
      </div>
      
      <Card className="card-scheme-ruby-red">
        <CardHeader className="pb-2">
          <CardTitle>Flagged Activities</CardTitle>
        </CardHeader>
        <CardContent>
          {flaggedActivities && flaggedActivities.length > 0 ? (
            isMobile ? (
              // Mobile card layout
              <div className="space-y-3">
                {flaggedActivities.map((activity) => (
                  <MobileFlaggedActivityCard
                    key={activity.id}
                    activity={activity}
                    onInvestigate={handleInvestigate}
                    onDismiss={handleDismiss}
                  />
                ))}
              </div>
            ) : (
              // Desktop table layout
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Affiliate</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flaggedActivities.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>{new Date(activity.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>{activity.affiliateName}</TableCell>
                        <TableCell>{activity.type}</TableCell>
                        <TableCell>
                          <span className="text-sm">{activity.reason}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            activity.status === 'new' 
                              ? 'bg-botscheme-ruby-red/20 text-botscheme-ruby-red' 
                              : activity.status === 'investigating'
                              ? 'bg-botscheme-amber-gold/20 text-botscheme-amber-gold'
                              : 'bg-botscheme-emerald-green/20 text-botscheme-emerald-green'
                          }>
                            {activity.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {activity.status !== 'resolved' && (
                            <>
                              {activity.status !== 'investigating' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleInvestigate(activity.id)}
                                >
                                  Investigate
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDismiss(activity.id)}
                              >
                                Dismiss
                              </Button>
                            </>
                          )}
                          {activity.status === 'resolved' && (
                            <span className="text-sm text-muted-foreground">Resolved</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              No flagged activities found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FlaggedActivityPage;
